import DateAndValue from "../DateAndValue";
import { DateTime } from "luxon";
import { ASCENDANT_ORDER, DESCENDANT_ORDER } from "../../config";

export default class CashFlow {
  constructor({
    dates,
    years,
    netProfit = [],
    monthlydepreciationAmortization = [],
    monthlyTotalAccountsReceivable = [],
    monthlyTotalInventory = [],
    monthlyTotalOtherCurrentAssets = [],
    monthlyTotalAccountsPayable = [],
    monthlyTotalOtherCurrentLiabilities = [],
    monthlyTotalGrossFixedAssets = [],
    monthlyTotalRealEstateAssets = [],
    monthlyTotalOtherLongTermAssets = [],
    monthlyTotalLongTermLiabilities = [],
    monthlyTotalLoansNotesPayable = [],
    monthlyTotalNonOperatingLongTermLiabilities = [],
    monthlyTotalOtherLongTermLiabilities = [],
    monthlyTotalMarketableSecurities = [],
    monthlyTotalEquity = [],
    monthlyTotalCash = [],
    dateOrder = DESCENDANT_ORDER,
  }) {
    this.dates = dates;
    this.years = years;
    this.netProfit = netProfit;
    this.monthlydepreciationAmortization = monthlydepreciationAmortization;
    this.monthlyTotalAccountsReceivable = monthlyTotalAccountsReceivable;
    this.monthlyTotalInventory = monthlyTotalInventory;
    this.monthlyTotalOtherCurrentAssets = monthlyTotalOtherCurrentAssets;
    this.monthlyTotalAccountsPayable = monthlyTotalAccountsPayable;
    this.monthlyTotalOtherCurrentLiabilities =
      monthlyTotalOtherCurrentLiabilities;
    this.monthlyTotalGrossFixedAssets = monthlyTotalGrossFixedAssets;
    this.monthlyTotalRealEstateAssets = monthlyTotalRealEstateAssets;
    this.monthlyTotalOtherLongTermAssets = monthlyTotalOtherLongTermAssets;
    this.monthlyTotalLongTermLiabilities = monthlyTotalLongTermLiabilities;
    this.monthlyTotalLoansNotesPayable = monthlyTotalLoansNotesPayable;
    this.monthlyTotalNonOperatingLongTermLiabilities =
      monthlyTotalNonOperatingLongTermLiabilities;
    this.monthlyTotalOtherLongTermLiabilities =
      monthlyTotalOtherLongTermLiabilities;
    this.monthlyTotalMarketableSecurities = monthlyTotalMarketableSecurities;
    this.monthlyTotalEquity = monthlyTotalEquity;
    this.monthlyTotalCash = monthlyTotalCash;
    this.dateOrder = dateOrder;
  }

  get ascendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return 1;
      if (a < b) return -1;
    });
  }

  get descendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return -1;
      if (a < b) return 1;
    });
  }

  get orderedDates() {
    if (this.dateOrder === ASCENDANT_ORDER) {
      return this.ascendantDates;
    }

    return this.descendantDates;
  }

  get datesFormatted() {
    return this.orderedDates.map((date) => {
      return DateTime.fromISO(date).toFormat("LLLL y");
    });
  }

  get yearsFormatted() {
    return this.dateOrder === ASCENDANT_ORDER ? this.years.sort() : this.years.sort().reverse();
  }

  get cashFromOperationsArray() {
    const {
      netProfit,
      monthlydepreciationAmortization,
      monthlyTotalAccountsReceivable,
      monthlyTotalInventory,
      monthlyTotalOtherCurrentAssets,
      monthlyTotalAccountsPayable,
      monthlyTotalOtherCurrentLiabilities,
    } = this;
    let resultArray = [];
    let netIncome = { name: "Net Income" };
    let depreciationAmortization = {
      name: "Depreciation, Depletion, Amortization",
    };
    let changeInAccountsReceivable = { name: "Change in Accounts Receivable" };
    let changeInInventory = { name: "Change in Inventory" };
    let changeInOtherCurrentAssets = { name: "Change in Other Current Assets" };
    let changeInAccountsPayable = { name: "Change in Accounts Payable" };
    let changeInOtherCurrentLiabilities = {
      name: "Change in Other Current Liabilities",
    };

    //put netIncome to cashFromOperatingArray if not empty
    netIncome.array = this.getStructuredArray(netProfit, 0);
    if (netIncome.array.length > 0) resultArray.push(netIncome);

    //put depreciationAmortization to cashFromOperatingArray if not empty
    depreciationAmortization.array = this.getStructuredArray(
      monthlydepreciationAmortization,
      0
    );
    if (depreciationAmortization.array.length > 0)
      resultArray.push(depreciationAmortization);

    //put changeInAccountsReceivable to cashFromOperatingArray if not empty
    changeInAccountsReceivable.array = this.getStructuredArray(
      monthlyTotalAccountsReceivable,
      2
    );
    if (changeInAccountsReceivable.array.length > 0)
      resultArray.push(changeInAccountsReceivable);

    //put changeInInventory to cashFromOperatingArray if not empty
    changeInInventory.array = this.getStructuredArray(monthlyTotalInventory, 2);
    if (changeInInventory.array.length > 0) resultArray.push(changeInInventory);

    //put changeInOtherCurrentAssets to cashFromOperatingArray if not empty
    changeInOtherCurrentAssets.array = this.getStructuredArray(
      monthlyTotalOtherCurrentAssets,
      2
    );
    if (changeInOtherCurrentAssets.array.length > 0)
      resultArray.push(changeInOtherCurrentAssets);

    //put changeInAccountsPayable to cashFromOperatingArray if not empty
    changeInAccountsPayable.array = this.getStructuredArray(
      monthlyTotalAccountsPayable,
      1
    );
    if (changeInAccountsPayable.array.length > 0)
      resultArray.push(changeInAccountsPayable);

    //put changeInOtherCurrentLiabilities to cashFromOperatingArray if not empty
    changeInOtherCurrentLiabilities.array = this.getStructuredArray(
      monthlyTotalOtherCurrentLiabilities,
      1
    );
    if (changeInOtherCurrentLiabilities.array.length > 0)
      resultArray.push(changeInOtherCurrentLiabilities);

    return resultArray;
  }

  get yearlyCashFromOperationsArray() {
    const { cashFromOperationsArray } = this;
    return cashFromOperationsArray.map((obj) => 
      ({ name: obj.name, array: this.calculateTotalByYear(obj.array)})
    )
  }

  get cashFromInvestingArray() {
    const {
      monthlyTotalGrossFixedAssets,
      monthlyTotalRealEstateAssets,
      monthlydepreciationAmortization,
      monthlyTotalOtherLongTermAssets,
    } = this;
    let resultArray = [];
    let changeInNetFixedAssets = { name: "Change in Net Fixed Assets" };
    let changeInRealEstateAssets = { name: "Change in Real Estate Assets" };
    let depreciationAmortization = {
      name: "Depreciation, , Amortization",
    };
    let changeInGrossFixedAssets = { name: "Change in Gross Fixed Assets" };
    let changeInOtherLongTermAssets = {
      name: "Change in Other Long Term Assets",
    };

    //put changeInGrossFixedAssets to cashFromInvestingArray if not empty
    changeInNetFixedAssets.array = this.getStructuredArray(
      monthlyTotalGrossFixedAssets,
      2
    );
    changeInRealEstateAssets.array = this.getStructuredArray(
      monthlyTotalRealEstateAssets,
      2
    );
    depreciationAmortization.array = this.getStructuredArray(
      monthlydepreciationAmortization,
      0
    );
    changeInGrossFixedAssets.array = this.getArraySum(
      this.getArraySum(
        changeInNetFixedAssets.array,
        changeInRealEstateAssets.array,
        1
      ),
      depreciationAmortization.array,
      0
    ); //changeInGrossFixedAssets = changeInNetFixedAssets + changeInRealEstateAssets - depreciationAmortization
    if (changeInGrossFixedAssets.array.length > 0)
      resultArray.push(changeInGrossFixedAssets);

    //put changeInOtherLongTermAssets to cashFromInvestingArray if not empty
    changeInOtherLongTermAssets.array = this.getStructuredArray(
      monthlyTotalOtherLongTermAssets,
      2
    );
    if (changeInOtherLongTermAssets.array.length > 0)
      resultArray.push(changeInOtherLongTermAssets);

    return resultArray;
  }

  get yearlyCashFromInvestingArray() {
    const { cashFromInvestingArray } = this;
    return cashFromInvestingArray.map((obj) => 
      ({ name: obj.name, array: this.calculateTotalByYear(obj.array)})
    )
  }

  get cashFromFinancingArray() {
    const {
      monthlyTotalLongTermLiabilities,
      monthlyTotalLoansNotesPayable,
      monthlyTotalNonOperatingLongTermLiabilities,
      monthlyTotalOtherLongTermLiabilities,
      monthlyTotalMarketableSecurities,
      monthlyTotalEquity,
      netProfit,
    } = this;
    let resultArray = [];
    let changeInLongTermLiabilities = {
      name: "Change in Long Term Liabilities",
    };
    let changeInLoansNotesPayable = { name: "Change in Loans/Notes Payable" };
    let changeInNonOperatingLongTermLiabilities = {
      name: "Change in Non-Operating Long Term Liabilities",
    };
    let changeInOtherLongTermLiabilities = {
      name: "Change in Other Long Term Liabilities",
    };
    let changeInNonOperOtherLongTermLiabilities = {
      name: "Change in Other Long Term Liability, Non-Operating Long Term Liabilities",
    };
    let changeInMarketableSecurities = {
      name: "Change in Marketable Securities",
    };
    let changeInMonthlyTotalEquity = { name: "Change in Monthly Total Equity" };
    let netIncome = { name: "Net Income" };
    let changeInTotalEquity = { name: "Change in Total Equity" };

    //put changeInLongTermLiabilities to cashFromFinancingArray if not empty
    changeInLongTermLiabilities.array = this.getStructuredArray(
      monthlyTotalLongTermLiabilities,
      1
    );
    if (changeInLongTermLiabilities.array.length > 0)
      resultArray.push(changeInLongTermLiabilities);

    //put changeInLoansNotesPayable to cashFromFinancingArray if not empty
    changeInLoansNotesPayable.array = this.getStructuredArray(
      monthlyTotalLoansNotesPayable,
      1
    );
    if (changeInLoansNotesPayable.array.length > 0)
      resultArray.push(changeInLoansNotesPayable);

    //put changeInNonOperOtherLongTermLiabilities to cashFromFinancingArray if not empty
    changeInNonOperatingLongTermLiabilities.array = this.getStructuredArray(
      monthlyTotalNonOperatingLongTermLiabilities,
      1
    );
    changeInOtherLongTermLiabilities.array = this.getStructuredArray(
      monthlyTotalOtherLongTermLiabilities,
      1
    );
    changeInNonOperOtherLongTermLiabilities.array = this.getArraySum(
      changeInNonOperatingLongTermLiabilities.array,
      changeInOtherLongTermLiabilities.array,
      1
    ); //changeInNonOperOtherLongTermLiabilities = changeInNonOperatingLongTermLiabilities + changeInOtherLongTermLiabilities
    if (changeInNonOperOtherLongTermLiabilities.array.length > 0)
      resultArray.push(changeInNonOperOtherLongTermLiabilities);

    //put changeInMarketableSecurities to cashFromFinancingArray if not empty
    changeInMarketableSecurities.array = this.getStructuredArray(
      monthlyTotalMarketableSecurities,
      2
    );
    if (changeInMarketableSecurities.array.length > 0)
      resultArray.push(changeInMarketableSecurities);

    //put changeInTotalEquity to cashFromFinancingArray if not empty
    changeInMonthlyTotalEquity.array = this.getStructuredArray(
      monthlyTotalEquity,
      1
    );
    netIncome.array = this.getStructuredArray(netProfit, 0);
    changeInTotalEquity.array = this.getArraySum(
      changeInMonthlyTotalEquity.array,
      netIncome.array,
      0
    ); //changeInTotalEquity = changeInMonthlyTotalEquity - netIncome
    if (changeInTotalEquity.array.length > 0)
      resultArray.push(changeInTotalEquity);

    return resultArray;
  }

  get yearlyCashFromFinancingArray() {
    const { cashFromFinancingArray } = this;
    return cashFromFinancingArray.map((obj) => 
      ({ name: obj.name, array: this.calculateTotalByYear(obj.array)})
    )
  }

  get totalCashFromOperations() {
    const { cashFromOperationsArray } = this;
    return this.getTotalCash(cashFromOperationsArray);
  }

  get yearlyTotalCashFromOperations() {
    const { totalCashFromOperations } = this;
    return this.calculateTotalByYear(totalCashFromOperations);
  }

  get totalCashFromInvesting() {
    const { cashFromInvestingArray } = this;
    return this.getTotalCash(cashFromInvestingArray);
  }

  get yearlyTotalCashFromInvesting() {
    const { totalCashFromInvesting } = this;
    return this.calculateTotalByYear(totalCashFromInvesting);
  }

  get totalCashFromFinancing() {
    const { cashFromFinancingArray } = this;
    return this.getTotalCash(cashFromFinancingArray);
  }

  get yearlyTotalCashFromFinancing() {
    const { totalCashFromFinancing } = this;
    return this.calculateTotalByYear(totalCashFromFinancing);
  }

  get openingCashBalance() {
    const { closingCashBalance, ascendantDates } = this;
    let ascClosingCashBalance = this.ascSort(closingCashBalance);
    let resultArray = [];
    let len = ascendantDates.length; // length = length of time series

    //put the first month value zero, disabled
    resultArray[0] = new DateAndValue({
      date: ascendantDates[0],
      value: 0,
      disabled: true,
    });

    for (let i = 1; i < len; i++) {
      resultArray[i] = new DateAndValue({
        date: ascendantDates[i],
        value: ascClosingCashBalance[i - 1].value,
        disabled: false,
      });
    }

    if (this.dateOrder === DESCENDANT_ORDER)
      return resultArray.reverse(); //we sort array according to date order
    else return resultArray;
  }

  get yearlyOpeningCashBalance() {
    const { yearlyClosingCashBalance, years } = this;
    let ascClosingCashBalance = this.ascSort(yearlyClosingCashBalance);
    let resultArray = [];
    let len = years.length; // length = length of time series

    //put the first month value zero, disabled
    resultArray[0] = new DateAndValue({
      date: years.sort()[0],
      value: 0,
      disabled: true,
    });

    for (let i = 1; i < len; i++) {
      resultArray[i] = new DateAndValue({
        date: years.sort()[i],
        value: ascClosingCashBalance[i - 1].value,
        disabled: false,
      });
    }

    if (this.dateOrder === DESCENDANT_ORDER)
      return resultArray.reverse(); //we sort array according to date order
    else return resultArray;
  }

  get netChangeInCash() {
    const {
      totalCashFromOperations,
      totalCashFromInvesting,
      totalCashFromFinancing,
    } = this;
    return this.getArraySum(
      this.getArraySum(totalCashFromOperations, totalCashFromInvesting, 1),
      totalCashFromFinancing,
      1
    );
  }

  get yearlyNetChangeInCash() {
    const { netChangeInCash } = this;
    return this.calculateTotalByYear(netChangeInCash);
  }

  get closingCashBalance() {
    const { monthlyTotalCash, netChangeInCash, ascendantDates } = this;
    const ascMonthlyTotalCash = this.ascSort(monthlyTotalCash);
    const ascNetChangeInCash = this.ascSort(netChangeInCash);
    let resultArray = [];
    let len = ascendantDates.length; // length = length of time series

    //first month value of closingCashBalance = first month value of monthlyTotalCash
    resultArray[0] = new DateAndValue({
      date: ascendantDates[0], //in case the monthlyTotalCash is empty
      value: monthlyTotalCash.length > 0 ? ascMonthlyTotalCash[0].value : 0, //in case the monthlyTotalCash is empty
      disabled: false,
    });

    //closingCashBalance = closingCashBalance of earlier month + monthlyTotalCash
    for (let i = 1; i < len; i++) {
      resultArray[i] = new DateAndValue({
        date: ascendantDates[i], //in case the netChangeInCash is empty
        value:
          netChangeInCash.length > 0
            ? resultArray[i - 1].value + ascNetChangeInCash[i].value
            : resultArray[i - 1].value, //in case the netChangeInCash is empty
        disabled: false,
      });
    }

    if (this.dateOrder === DESCENDANT_ORDER)
      return resultArray.reverse(); //we sort array according to date order
    else return resultArray;
  }

  get yearlyClosingCashBalance() {
    const { closingCashBalance } = this;
    return this.calculateTotalByYear(closingCashBalance, { sum: false });
  }

  getStructuredArray(series, direction) {
    //getStructuredArray calculates the differences of month values if direction > 0, return month value if direction = 0
    //series means each item time series, direction means how to calculate margin or whether return month value itself

    let len = series.length;
    if (len < 1) return [];
    let ascSeries = this.ascSort(series); //we must calculate margins according to date ascendant order
    let result = [];

    for (let i = 0; i < len; i++) {
      result[i] = new DateAndValue({
        date: ascSeries[i].date,
        value: 0,
        disabled: false,
      });
      if (direction === 0)
        result[i].value = ascSeries[i].value; //return this month value
      else if (direction === 1)
        result[i].value = i ? ascSeries[i].value - ascSeries[i - 1].value : ascSeries[i].value;
        //later month value - earlier month value. if it is the first element, earlier one is zero
      else if (direction === 2)
        result[i].value = i ? ascSeries[i - 1].value - ascSeries[i].value : -ascSeries[i].value;
        //earlier month value - later month value. if it is the first element, earlier one is zero
    }

    if (this.dateOrder === DESCENDANT_ORDER)
      return result.reverse(); //we sort array according to date order
    else return result;
  }

  getArraySum(array1, array2, positive) {
    //get the style of arrays, adding or subtracting values of arrays
    //positive = 1: add(+), positive = 0: subtract(-)
    let len1 = array1.length;
    let len2 = array2.length;
    let len; //length of result array
    let result = [];
    if (len2 < 1) return array1; //no need to calculate when array2 is empty

    len = len2;
    if (len1 < 1) {
      for (let i = 0; i < len; i++)
        array1[i] = new DateAndValue({
          date: 0,
          value: 0,
          disabled: false,
        });
    } //if array1 is empty, fill elements with zero values

    //add two array elements
    for (let i = 0; i < len; i++) {
      let value;
      if (positive) value = array1[i].value + array2[i].value; //array1 + array2
      else value = array1[i].value - array2[i].value; //array1 - array2
      result[i] = new DateAndValue({
        date: array2[i].date,
        value,
        disabled: array2[i].disabled,
      });
    }

    return result;
  }

  getTotalCash(itemsArray) {
    //get total cash from an array of items
    const { dates } = this;
    return dates.map(
      (date, dateIndex) =>
        new DateAndValue({
          date: itemsArray[0].array[dateIndex].date,
          value: itemsArray.reduce(
            (acc, itemObj) => (acc += itemObj.array[dateIndex].value),
            0
          ),
          disabled: itemsArray[0].array[dateIndex].disabled,
        })
    );
  }

  calculateTotalByYear(data, options = { sum: true }) {
    const yearTotalMap = {};
    let year = this.descendantDates[0];
    year = typeof year === "string" ? new Date(year) : year;
    if (this.dateOrder === "ASCENDANT_ORDER") data = data.reverse();

    for (const item of data) {
      const value = item.value;

      if (!isNaN(value)) {
        if (!yearTotalMap[year.toLocaleDateString()]) {
          yearTotalMap[year.toLocaleDateString()] = {
            sum: 0,
            count: 0,
          };
        }
        if(options.sum || yearTotalMap[year.toLocaleDateString()].count === 0)
          yearTotalMap[year.toLocaleDateString()].sum += value;
          //we get the sum of 12 months if option.sum is true, otherwise, get the last month
        yearTotalMap[year.toLocaleDateString()].count++;
        if (yearTotalMap[year.toLocaleDateString()].count === 12)
          year.setFullYear(year.getFullYear() - 1);
      }
    }

    const result = Object.entries(yearTotalMap).map(
      ([year, { sum, count }]) => ({
        date: DateTime.fromJSDate(new Date(year)).toFormat("LLLL y"),
        value: sum,
      })
    );
    
    return this.getOrderedList(result);
  }

  getOrderedList(list) {
    if(list.length === 0) return this.orderedDates.map((date) => new DateAndValue({
      date: date,
      value: 0,
    }));  //we need it in order to avoid empty arrays

    if (this.dateOrder === ASCENDANT_ORDER) {
      return list.sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
      });
    }

    return list.sort((a, b) => {
      if (a.date < b.date) return 1;
      if (a.date > b.date) return -1;
    });
  }

  ascSort(series) {
    //sort monthly time series by ascendant order
    return series.sort((a, b) => {
      if (a.date > b.date) return 1;
      if (a.date < b.date) return -1;
    });
  }
}
